import { useParams, useNavigate } from 'react-router-dom';
import { usePronunciationPractice } from '../hooks/usePronunciationPractice';
import Button from '../components/ui/Button';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import PronunciationScorer from '../components/PronunciationScorer';
import WordInfo from '../components/practice/WordInfo';
import UserProgress from '../components/practice/UserProgress';
import LastFeedback from '../components/practice/LastFeedback';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

const CelebrationAnimation = () => (
    <AnimatePresence>
        <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.2 }}
            transition={{ duration: 0.5, type: 'spring' }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        >
            <div className="bg-white rounded-3xl p-8 text-center shadow-2xl">
                <motion.div
                    animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.2, 1] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                    className="text-8xl mb-4"
                >
                    🎉
                </motion.div>
                <h3 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-blue-500 mb-2">
                    Excellent!
                </h3>
                <p className="text-lg text-gray-600">Amazing pronunciation!</p>
            </div>
        </motion.div>
    </AnimatePresence>
);

const ErrorDisplay = ({ message }: { message: string }) => (
    <div className="min-h-screen bg-red-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-3xl shadow-2xl border-4 border-red-200">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertTriangle className="w-12 h-12 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-red-800 mb-2">Oops! Something went wrong.</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <Button onClick={() => window.location.href = '/words'} variant="secondary">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Words
            </Button>
        </div>
    </div>
);

const LoadingDisplay = () => (
    <div className="min-h-screen bg-blue-50 flex items-center justify-center">
        <div className="text-center">
            <LoadingSpinner size="lg" className="mb-4" />
            <p className="text-lg text-blue-700 font-semibold animate-pulse">Loading your word...</p>
        </div>
    </div>
);

export default function PronunciationPracticePage() {
    const { wordId } = useParams<{ wordId: string }>();
    const navigate = useNavigate();
    const {
        wordData,
        error,
        isLoading,
        sessionId,
        currentScore,
        isCelebrating,
        handleScoreReceived,
    } = usePronunciationPractice(wordId!);

    if (isLoading) return <LoadingDisplay />;
    if (error) return <ErrorDisplay message={error.message} />;

    const { word, userProgress, lastFeedback } = wordData!;

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-100 font-sans">
            {isCelebrating && <CelebrationAnimation />}

            <div className="container mx-auto px-4 py-8 max-w-5xl">
                <div className="mb-8">
                    <Button
                        variant="outline"
                        onClick={() => navigate('/words')}
                        className="flex items-center gap-2 rounded-full px-6 py-3 shadow-sm hover:shadow-md transition-shadow"
                    >
                        <ArrowLeft className="w-5 h-5" />
                        Back to Word List
                    </Button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    <div className="lg:col-span-2 space-y-8">
                        <WordInfo word={word} />
                        {lastFeedback && <LastFeedback lastFeedback={lastFeedback} />}
                        {userProgress && <UserProgress progress={userProgress} />}
                    </div>

                    <div className="lg:col-span-3">
                        <PronunciationScorer
                            word={word.word}
                            wordId={word.id}
                            sessionId={sessionId}
                            onScoreReceived={handleScoreReceived}
                            currentScore={currentScore}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}